/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * GA4 DataLayer Utilities
 *
 * Este módulo centraliza toda a lógica de tracking GA4:
 * - Push de eventos para o dataLayer (client-side via GTM)
 * - Helpers para obter client_id e user_id
 */

// ============================================
// Debug Mode
// ============================================

/**
 * Ativa o debug_mode quando estiver em ambiente de desenvolvimento
 * ou quando a URL tiver o parâmetro ?ga_debug=true
 */
function isDebugMode(): boolean {
  const isDev = process.env.NODE_ENV === 'development';
  const hasParam =
    typeof window !== 'undefined' &&
    window.location.search.includes('ga_debug=true');
  return isDev || hasParam;
}

// ============================================
// Interfaces
// ============================================

export interface IGA4Item {
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
}

export interface IGA4EventParams {
  [key: string]: any;
}

// ============================================
// Client ID Management
// ============================================

/**
 * Obtém o GA4 client_id do cookie _ga.
 * O cookie _ga tem o formato: GA1.1.XXXXXXXXXX.XXXXXXXXXX
 * O client_id é composto pelos dois últimos segmentos.
 */
export function getGA4ClientId(): string {
  try {
    const cookie = document.cookie
      .split('; ')
      .find((row) => row.startsWith('_ga='));

    if (cookie) {
      const parts = cookie.split('.');
      if (parts.length >= 4) {
        return `${parts[2]}.${parts[3]}`;
      }
    }
  } catch (error) {
    // silently fail
  }

  // Fallback: gera um client_id temporário
  const timestamp = Math.floor(Date.now() / 1000);
  const random = Math.floor(Math.random() * 1000000000);
  return `${random}.${timestamp}`;
}

/**
 * Obtém o user_id do localStorage (se logado).
 */
export function getGA4UserId(): string | undefined {
  try {
    const userData = localStorage.getItem('@Koro:user');
    if (userData) {
      const user = JSON.parse(userData);
      return user.id ? String(user.id) : undefined;
    }
  } catch (error) {
    // silently fail
  }
  return undefined;
}

// ============================================
// DataLayer Push (Client-Side via GTM)
// ============================================

/**
 * Push genérico para o dataLayer do GTM.
 */
function pushToDataLayer(data: Record<string, any>): void {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(data);
}

/**
 * Dispara evento GA4 padronizado via dataLayer.
 */
export function trackGA4Event(
  eventName: string,
  params: IGA4EventParams = {}
): void {
  const clientId = getGA4ClientId();
  const userId = getGA4UserId();
  const debug = isDebugMode();

  const eventData: Record<string, any> = {
    event: 'koro_event',
    event_name: eventName,
    client_id: clientId,
    user_id: userId || undefined,
    platform: 'koro',
    ...(debug ? { debug_mode: true } : {}),
    ...params,
  };

  pushToDataLayer(eventData);

  // Log visual no console em modo debug
  if (debug) {
    /* eslint-disable no-console */
    console.groupCollapsed(
      `%c[GA4] ${eventName}`,
      'color: #ff6900; font-weight: bold; font-size: 12px;'
    );
    console.log('📦 Payload:', eventData);
    console.log('🆔 client_id:', clientId);
    console.log('👤 user_id:', userId || '(não logado)');
    console.log('⏱️ timestamp:', new Date().toISOString());
    console.groupEnd();
    /* eslint-enable no-console */
  }
}

// ============================================
// Helpers
// ============================================

function getPageCategory(pathname: string): string {
  if (pathname === '/') return 'home';
  if (pathname.includes('/login') || pathname.includes('/cadastro'))
    return 'auth';
  if (
    pathname.includes('/categorias') ||
    pathname.includes('/titularidade') ||
    pathname.includes('/resumo')
  )
    return 'checkout';
  if (pathname.includes('/eventos/')) return 'event';
  if (pathname.includes('/meus-ingressos')) return 'tickets';
  if (pathname.includes('/perfil')) return 'profile';
  if (pathname.includes('/buscar-eventos')) return 'search';
  return 'other';
}

// ============================================
// Eventos Específicos (Client-Side)
// ============================================

/**
 * page_view - Disparado a cada mudança de rota.
 */
export function trackPageView(pageTitle?: string, pageLocation?: string): void {
  trackGA4Event('page_view', {
    page_title: pageTitle || document.title,
    page_location: pageLocation || window.location.href,
    page_category: getPageCategory(window.location.pathname),
  });
}

/**
 * view_item - Quando o usuário visualiza a página de um evento.
 */
export function trackViewItem(item: IGA4Item, eventSlug?: string): void {
  pushToDataLayer({ ecommerce: null }); // Limpa ecommerce anterior
  trackGA4Event('view_item', {
    page_category: 'event',
    ecommerce: {
      currency: 'BRL',
      value: item.price,
      items: [item],
    },
    event_slug: eventSlug,
  });
}

/**
 * select_item - Quando o usuário seleciona uma categoria/modalidade.
 */
export function trackSelectItem(item: IGA4Item): void {
  pushToDataLayer({ ecommerce: null });
  trackGA4Event('select_item', {
    page_category: 'checkout',
    ecommerce: {
      currency: 'BRL',
      items: [item],
    },
  });
}

/**
 * add_to_cart - Quando o usuário adiciona ingressos ao carrinho.
 */
export function trackAddToCart(items: IGA4Item[], value: number): void {
  pushToDataLayer({ ecommerce: null });
  trackGA4Event('add_to_cart', {
    page_category: 'checkout',
    ecommerce: {
      currency: 'BRL',
      value,
      items,
    },
  });
}

/**
 * begin_checkout - Quando o usuário inicia o checkout (tela de pagamento).
 */
export function trackBeginCheckout(items: IGA4Item[], value: number): void {
  pushToDataLayer({ ecommerce: null });
  trackGA4Event('begin_checkout', {
    page_category: 'checkout',
    ecommerce: {
      currency: 'BRL',
      value,
      items,
    },
  });
}

/**
 * purchase - Quando o pagamento é confirmado (client-side).
 */
export function trackPurchase(
  transactionId: string,
  items: IGA4Item[],
  value: number,
  additionalParams: IGA4EventParams = {}
): void {
  pushToDataLayer({ ecommerce: null });
  trackGA4Event('purchase', {
    page_category: 'checkout',
    ecommerce: {
      transaction_id: transactionId,
      currency: 'BRL',
      value,
      items,
      ...additionalParams,
    },
  });
}

/**
 * login - Quando o usuário faz login.
 */
export function trackLogin(method: string): void {
  trackGA4Event('login', {
    method,
    page_category: 'auth',
  });
}

/**
 * sign_up - Quando o usuário se cadastra.
 */
export function trackSignUp(method: string): void {
  trackGA4Event('sign_up', {
    method,
    page_category: 'auth',
  });
}
